import React, { useState, useEffect, useCallback } from 'react';
import './AccountSharing.css';
import { API_BASE_URL } from '../config';
import { formatDateTime } from '../utils/timeFormat';

interface LinkedUser {
  id: string;
  username: string;
  email: string;
  linked_at: string;
}

interface PendingInvite {
  id: string;
  recipient_email: string | null;
  code: string;
  expires_at: string;
  created_at: string;
  used: boolean;
}

interface AccountSharingProps {
  user: any;
}

const AccountSharing: React.FC<AccountSharingProps> = ({ user }) => {
  const [linkedUsers, setLinkedUsers] = useState<LinkedUser[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [acceptCode, setAcceptCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [linkedResponse, pendingResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/links/linked-users?user_id=${user.id}`),
        fetch(`${API_BASE_URL}/api/links/pending-invites?user_id=${user.id}`)
      ]);

      if (linkedResponse.ok) {
        const linkedData = await linkedResponse.json();
        setLinkedUsers(linkedData);
      }

      if (pendingResponse.ok) {
        const pendingData = await pendingResponse.json();
        setPendingInvites(pendingData);
      }
    } catch (error) {
      console.error('Failed to fetch linking data:', error);
      setError('Failed to load account sharing information');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sendInvite = async () => {
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/links/invite?sender_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_email: inviteEmail.trim() || null,
        }),
      });

      if (response.ok) {
        const inviteData = await response.json();
        if (inviteEmail.trim()) {
          setSuccess(`Code generated and sent to ${inviteEmail}! Code: ${inviteData.code}`);
        } else {
          setSuccess(`Code generated: ${inviteData.code}. Share this code with anyone you want to link accounts with.`);
        }
        setInviteEmail('');
        fetchData(); // Refresh pending invites
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to generate invite');
      }
    } catch (error) {
      setError('Failed to generate invite');
    } finally {
      setIsSubmitting(false);
    }
  };

  const acceptInvite = async () => {
    if (!acceptCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/links/accept?user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: acceptCode.trim(),
        }),
      });

      if (response.ok) {
        setSuccess('Successfully linked accounts! You can now see shared babies.');
        setAcceptCode('');
        fetchData(); // Refresh linked users
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to accept invite');
      }
    } catch (error) {
      setError('Failed to accept invite');
    } finally {
      setIsSubmitting(false);
    }
  };

  const unlinkUser = async (linkedUserId: string, username: string) => {
    if (!window.confirm(`Are you sure you want to unlink from ${username}? This will remove access to their babies.`)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/links/unlink/${linkedUserId}?user_id=${user.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSuccess('Successfully unlinked user');
        fetchData(); // Refresh linked users
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to unlink user');
      }
    } catch (error) {
      setError('Failed to unlink user');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="account-sharing-loading">Loading account sharing...</div>;
  }

  return (
    <div className="account-sharing">
      <p className="section-description">
        Share baby accounts with family members or caregivers. Generate invite codes that anyone can use
        to link their account with yours. When accounts are linked, you'll see each other's babies and sleep records.
      </p>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Generate Invite Section */}
      <div className="sharing-section">
        <h4>Generate Invitation Code</h4>
        {(user.is_anonymous || !user.email) ? (
          <div className="anonymous-restriction">
            <p className="restriction-message">
              You need to add an email address to your account before you can share with others.
            </p>
            <p className="restriction-hint">
              Go to the Account section above to add your email address.
            </p>
          </div>
        ) : (
          <>
            <p>Generate a code that anyone can use to link their account. Optionally send it via email.</p>
            <div className="invite-form">
              <input
                type="email"
                placeholder="Email address (optional)"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={isSubmitting}
              />
              <button
                onClick={sendInvite}
                className="send-invite-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Generating...' : 'Generate Code'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Accept Invite Section */}
      <div className="sharing-section">
        <h4>Accept Invitation</h4>
        <p>Enter a 14-digit code to link accounts. The code can be shared via email, text, or any other way.</p>
        <div className="accept-form">
          <input
            type="text"
            placeholder="Enter 14-digit code"
            value={acceptCode}
            onChange={(e) => setAcceptCode(e.target.value.replace(/\D/g, '').slice(0, 14))}
            disabled={isSubmitting}
            maxLength={14}
          />
          <button 
            onClick={acceptInvite}
            className="accept-invite-btn"
            disabled={isSubmitting || acceptCode.length !== 14}
          >
            {isSubmitting ? 'Accepting...' : 'Accept Invite'}
          </button>
        </div>
      </div>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div className="sharing-section">
          <h4>Generated Invitation Codes</h4>
          <div className="pending-invites">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="pending-invite">
                <div className="invite-info">
                  <strong>{invite.recipient_email || 'General invite code'}</strong>
                  <p>Code: <code>{invite.code}</code></p>
                  <p>Expires: {formatDateTime(invite.expires_at, user.time_format_24h)}</p>
                  {invite.used && (
                    <p style={{color: 'var(--success-color)'}}>✓ Used</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Linked Users */}
      {linkedUsers.length > 0 && (
        <div className="sharing-section">
          <h4>Linked Accounts</h4>
          <div className="linked-users">
            {linkedUsers.map((linkedUser) => (
              <div key={linkedUser.id} className="linked-user">
                <div className="user-info">
                  <strong>{linkedUser.username}</strong>
                  <p>{linkedUser.email}</p>
                  <p>Linked: {new Date(linkedUser.linked_at).toLocaleDateString()}</p>
                </div>
                <button 
                  onClick={() => unlinkUser(linkedUser.id, linkedUser.username)}
                  className="unlink-btn"
                  disabled={isSubmitting}
                >
                  Unlink
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {linkedUsers.length === 0 && pendingInvites.length === 0 && (
        <div className="no-sharing">
          <p>No linked accounts or generated invitation codes.</p>
        </div>
      )}
    </div>
  );
};

export default AccountSharing;